import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { getAllPosts } from '../../api/posts'

const LatestUpdatesSection = () => {
  // Fetch latest posts
  const { data: postsData, isLoading, error } = useQuery({
    queryKey: ['latestPosts'],
    queryFn: () => getAllPosts({ 
      status: 'published',
      limit: 5,
      sortBy: 'created_at',
      sortOrder: 'desc'
    }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const posts = postsData?.data?.posts || []

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const stripHtml = (html) => {
    const div = document.createElement('div')
    div.innerHTML = html
    return div.textContent || div.innerText || ''
  }

  return (
    <div className="updates-section">
      <div className="updates-header">
        <h3 className="section-title">Latest Updates</h3>
        <Link to="/dashboard/posts" className="see-more">
          See more
        </Link>
      </div>
      <div className="updates-list">
        {isLoading ? (
          <div className="update-item">
            <p className="update-text">Loading latest updates...</p>
          </div>
        ) : error ? (
          <div className="update-item">
            <p className="update-text text-danger">Error loading updates</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="update-item">
            <p className="update-text">No updates available</p>
          </div>
        ) : (
          posts.map((post) => (
            <Link
              key={post.id}
              to={`/dashboard/posts/${post.slug || post.id}`}
              className="update-item update-link"
            >
              <div className="update-header">
                <h6 className="update-title">{post.title}</h6>
                <span className="update-date">{formatDate(post.created_at)}</span>
              </div>
              <p className="update-text">
                {post.excerpt || (stripHtml(post.content).length > 150 
                  ? stripHtml(post.content).substring(0, 150) + '...' 
                  : stripHtml(post.content))}
              </p>
            
              <div className="read-more">
                <span>Read more →</span>
              </div>
            </Link>
          ))
        )}
      </div>

      <style jsx>{`
        .updates-section {
          background: white;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        .updates-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .section-title {
          font-size: 18px;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0;
        }

        .see-more {
          color: #4A90E2;
          font-size: 14px;
          cursor: pointer;
          text-decoration: none;
        }

        .see-more:hover {
          text-decoration: underline;
        }

        .updates-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
          flex: 1;
        }

        .update-item {
          padding-bottom: 16px;
          border-bottom: 1px solid #eee;
        }

        .update-item:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        .update-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0;
        }

        .update-title {
          font-size: 16px;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0;
          flex: 1;
        }

        .update-date {
          font-size: 12px;
          color: #999;
          white-space: nowrap;
          margin-left: 12px;
        }

        .text-danger {
          color: #dc3545;
        }

        .update-link {
          text-decoration: none;
          color: inherit;
          transition: all 0.2s ease;
        }

        .update-link:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .update-link:hover .update-title {
          color: #4A90E2;
        }

        .read-more {
          margin-top: 12px;
          display: flex;
          justify-content: flex-end;
          opacity: 0;
          transition: opacity 0.2s ease;
        }

        .update-link:hover .read-more {
          opacity: 1;
        }

        .read-more span {
          color: #4A90E2;
          font-size: 12px;
          font-weight: 500;
        }
      `}</style>
    </div>
  )
}

export default LatestUpdatesSection 